/**
 * Per-route <head>. The prerenderer writes title/description/canonical into the
 * static HTML from seo.json; this keeps the SPA honest after client navigation,
 * and carries the JSON-LD that seo.json has no field for.
 *
 * Without a per-route canonical every page inherits the home one, and Google
 * treats the sub-pages as duplicates of the homepage and drops them.
 *
 * ⚠️ THE JSON-LD IS RENDERED, NOT APPENDED IN AN EFFECT — a fix, not a style
 * choice. It used to be built inside the `useEffect` below, which `renderToString`
 * never runs, so **every one of the 968 prerendered files shipped with no
 * structured data at all** while the plan's task table recorded P3.2 as done.
 * Measured 2026-08-09: `grep -c 'application/ld+json'` returned 0 on hymn, tune,
 * person and meter pages alike. The markup was right for the one visitor who
 * client-side navigated to a page and absent for every crawler that fetched it —
 * and a crawler is the only audience structured data has.
 *
 * A `<script type="application/ld+json">` in the body is valid HTML and is read
 * exactly as one in the head. Rendering it also makes SSR and the first client
 * render agree, so it cannot introduce the hydration mismatch that appending to
 * `document.head` was side-stepping.
 */
import { useEffect } from 'react'

import { serializeJsonLd } from '@/lib/json-ld'

const ORIGIN = 'https://fuguelab.com'

function upsertMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

export function PageHead({
  title,
  description,
  path,
  schema,
  // The build serves more than one book: `/hymnal/hymnal1982/*` is the hymnal and
  // `/hymnal/psalter/*` is the BCP psalter, so the suffix cannot be a constant.
  // It defaults to the hymnal because that is what every pre-existing caller means,
  // which keeps this a pure addition — but a psalm titled "| The Hymnal 1982" would
  // be wrong in the one place a title is load-bearing, the search result.
  suffix = 'The Hymnal 1982',
}: {
  title: string
  description: string
  path: string
  schema?: Record<string, unknown>
  suffix?: string
}) {
  useEffect(() => {
    document.title = `${title} | ${suffix}`
    upsertMeta('meta[name="description"]', 'name', 'description', description)

    const url = `${ORIGIN}${path}`
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    link.href = url
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title)
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', url)
  }, [title, description, path, suffix])

  if (!schema) return null
  // `dangerouslySetInnerHTML` is required, not lazy: React HTML-escapes normal
  // text children, and inside a <script> those entities are never decoded — the
  // JSON would ship corrupted. `serializeJsonLd` carries the escaping and its
  // reasoning, and is tested there because the corpus never exercises it.
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }} />
}
